import { useEffect } from 'react';
import { LinearProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { useDocuments } from '../../../../services/hooks';
import MarkdownViewer from '../../../FileViewer/markdown-viewer/MarkdownViewer';
import { DEFAULT_DOC_CONTENT_STALE_TIME_IN_MS } from '../document-preview.constants';
import { MarkdownViewerTabProps } from './MarkdownViewerTab.props';
import { EventType, useEventSubscription } from '../../../../context';
import { useCitationContext } from '../../../../pages/CapTable/cap-table/components/captable-table/CitationContext';
import { SelectionReference } from '../../../HighlightedText/highlightable-text-overlay/HighlightableTextOverlay.props';
import { useAttributeMappedWidgetDrawer } from '../../attribute-mapped-widget-drawer/AttributeMappedWidgetDrawerContext';

export const MarkdownViewerTab = ({ document }: MarkdownViewerTabProps) => {
  const { downloadDocumentContent } = useDocuments();
  const { onCitationPushed } = useAttributeMappedWidgetDrawer();
  const { data, isLoading } = useQuery({
    queryKey: ['document', document.document_reference_id, 'content'],
    enabled: !!document.document_reference_id,
    staleTime: DEFAULT_DOC_CONTENT_STALE_TIME_IN_MS,
    queryFn: () => downloadDocumentContent(document),
  });
  const { activeCitation, setActiveCitation } = useCitationContext();

  useEventSubscription(EventType.SPREADSHEET_ACTIVE_CELL_CHANGED, () => {
    setActiveCitation();
  });

  useEffect(() => {
    if (activeCitation && activeCitation?.document_reference !== document.document_reference_id) {
      setActiveCitation();
      enqueueSnackbar('Citation not found in currently open document, review citation number', { variant: 'info' });
    }
  }, [activeCitation, document.document_reference_id, setActiveCitation]);

  useEffect(() => {
    setActiveCitation();
  }, [setActiveCitation, document.document_reference_id]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && activeCitation) {
        setActiveCitation();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeCitation, setActiveCitation]);

  const handleCitationPushed = (
    payload: { selectedText: string; contextText?: string; startIndex: number; endIndex: number },
    selectionRange: SelectionReference
  ) => {
    onCitationPushed?.(payload, selectionRange, document.document_reference_id);
  };

  if (isLoading) {
    return <LinearProgress />;
  }

  if (!data.content && !data.blob_url) {
    return <div>No markdown document found</div>;
  }

  return (
    <MarkdownViewer
      base64Content={data.content}
      src={data.blob_url}
      onCitationPushed={handleCitationPushed}
      activeCitation={activeCitation}
    />
  );
};
